import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { GanttChart } from "./GanttChart";

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

const Kicker = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[11px] uppercase tracking-[0.22em] text-primary font-semibold mb-4">
    {children}
  </p>
);

const personaSignals = [
  "La empresa evolucionó, pero su posicionamiento quedó atrás.",
  "Tiene mucho valor, pero le cuesta explicarlo y hacerlo perceptible.",
  "El mercado la compara principalmente por producto, precio o condiciones comerciales.",
  "Su comunicación está fragmentada entre acciones, proveedores y canales.",
  "Distintas áreas de la empresa transmiten percepciones diferentes.",
  "El founder concentra buena parte de la confianza, reputación y relaciones.",
  "Necesita independizar el Capital de Marca del fundador o transferirlo a una nueva generación.",
  "Tiene trayectoria, pero marcas más nuevas parecen más relevantes o diferenciadas.",
  "Entra en una nueva etapa —expansión, profesionalización, sucesión, nuevos mercados—.",
];

const movements = [
  {
    name: "CONSTRUIR",
    text: "Cuando la empresa nunca convirtió su valor empresarial en una posición clara.",
  },
  {
    name: "ELEVAR",
    text: "Cuando la empresa evolucionó y la percepción del mercado quedó atrás.",
  },
  {
    name: "TRANSFERIR",
    text: "Cuando demasiado valor está concentrado en el founder, una generación o una unidad.",
  },
];

const journey = [
  {
    step: "Paso 1",
    title: "Diagnóstico de Capital de Marca",
    meta: "Semanas 1–2 · DWY",
    body: "Lectura integral de la empresa, su marca, liderazgo, comunicación, mercado y momento estratégico. Identificamos qué valor ya existe, qué percepción construye hoy y dónde están las brechas. Sobre todo, determinamos el movimiento prioritario: construir, elevar o transferir.",
    close:
      "La empresa deja de mirar síntomas aislados y entiende qué problema de Capital de Marca resolver primero y por qué.",
  },
  {
    step: "Paso 2",
    title: "Percepción Objetivo",
    meta: "Semanas 3–4 · DWY",
    body: "Definimos qué necesita representar la marca para acompañar el verdadero nivel y la próxima etapa: identidad estratégica, valor diferencial, atributos, posición deseada, públicos y asociaciones a fortalecer o modificar.",
    close: "La empresa cuenta con una Percepción Objetivo definida que funciona como norte estratégico.",
  },
  {
    step: "Paso 3",
    title: "Arquitectura de Capital de Marca",
    meta: "Semanas 5–6 · DWY",
    body: "Mapeamos dónde reside hoy el Capital de Marca —empresa, founder, productos, equipo, experiencia, reputación, comunicación, canales, alianzas y activos institucionales— y definimos qué fortalecer, qué alinear y qué transferir.",
    close: "La empresa entiende qué activos construyen su Capital de Marca y cómo deben relacionarse entre sí.",
  },
  {
    step: "Paso 4",
    title: "Dirección Estratégica",
    meta: "Semanas 7–8 · DWY + DFY",
    body: "Convertimos el diagnóstico en decisiones: dónde intervenir, en qué orden y para qué. Según el caso, la hoja de ruta puede involucrar posicionamiento, narrativa, arquitectura, identidad, experiencia, comunicación, founder brand, cultura, canales, propuesta de valor, sistema comercial o reputación.",
    close:
      "Prioridades estratégicas claras: la empresa deja de invertir recursos en acciones desconectadas entre sí.",
  },
  {
    step: "Paso 5",
    title: "Activación y Gobernanza",
    meta: "Semanas 8–9 · DWY + DIY",
    body: "Transformamos la estrategia en un sistema sostenible: criterios, responsables, prioridades, indicadores y hoja de ruta de implementación. El objetivo es que la marca tenga una dirección clara.",
    close:
      "Dirección y equipo saben qué construir, qué priorizar, qué medir y bajo qué criterios decidir.",
  },
];

const deliverables = [
  {
    title: "Diagnóstico de Capital de Marca",
    body: "Activos existentes, brechas de percepción, Capital No Capitalizado, inconsistencias, oportunidades, riesgos y movimiento prioritario.",
    solves:
      "Reemplaza el “hacemos muchas cosas pero no sabemos qué mueve la marca” por una lectura integral del problema.",
  },
  {
    title: "Mapa de Capital de Marca",
    body: "Representación de dónde reside el valor de la empresa y cómo se distribuye entre sus activos: concentrado, desaprovechado, fragmentado o transferible.",
    solves: "Hace visible algo que normalmente existe solo de manera intuitiva en la cabeza de los líderes.",
  },
  {
    title: "Percepción Objetivo",
    body: "Cómo necesita ser comprendida, reconocida y valorada la empresa: atributos, asociaciones, diferenciales y señales prioritarias a instalar.",
    solves:
      "Crea un norte común para que marketing, ventas, liderazgo y proveedores dejen de construir percepciones distintas.",
  },
  {
    title: "Plataforma Estratégica de Capital de Marca",
    body: "Posición, diferenciación, propuesta de valor, narrativa estratégica, públicos prioritarios, arquitectura, activos de marca y criterios de decisión.",
    solves: "Transforma el diagnóstico en una definición concreta de qué representa la empresa y qué construir.",
  },
  {
    title: "Hoja de Ruta de Capital de Marca",
    body: "Qué intervenir, por qué, en qué orden, con qué objetivo, quién debería hacerlo y cómo evaluar el avance.",
    solves: "Evita volver a la lógica de acciones aisladas y permite asignar tiempo, presupuesto y recursos con criterio.",
  },
  {
    title: "Sistema de Gobernanza de Marca",
    body: "Criterios de coherencia, responsables, niveles de decisión, indicadores, protocolo de evaluación y lineamientos para equipos y proveedores.",
    solves: "Convierte la estrategia en capacidad organizacional, no solamente en un documento.",
  },
  {
    title: "4 Sesiones de Dirección Estratégica",
    body: "Encuentros de 90 minutos con dirección y líderes clave. No son reuniones de seguimiento: son espacios de decisión donde se confrontan hipótesis y se definen prioridades.",
    solves:
      "La estrategia se construye junto a quienes dirigen el negocio, con metodología y perspectiva externa.",
  },
];

const results = [
  {
    name: "Óptica Spinelli",
    tag: "ELEVAR",
    text: "Sabrina Spinelli pasó de liderar un negocio con potencial pero sin dirección integrada a desarrollar una visión estratégica alineada entre empresa, equipo, marca y liderazgo. Lleva más de cuatro años renovando su trabajo con CORESSEN por crecimiento consecutivo.",
  },
  {
    name: "Hormicón",
    tag: "ELEVAR + TRANSFERIR",
    text: "Pasó de tener más de 40 años de valor diluido en una percepción limitada al hormigón elaborado, a definir una posición como constructora de soluciones integrales, con unidades de negocio en crecimiento.",
  },
  {
    name: "Luciana Gardella",
    tag: "CONSTRUIR / ORDENAR",
    text: "Pasó de una reputación profesional sólida distribuida entre distintas expresiones de marca a una arquitectura coherente y preparada para acompañar su crecimiento.",
  },
  {
    name: "Pastungui",
    tag: "CONSTRUIR",
    text: "Ale y Emi construyeron estratégicamente su Capital de Marca desde el nacimiento de la empresa y obtuvieron el premio a Mejor Branding de la Asociación Misionera de Marketing 2026.",
  },
  {
    name: "Carol Handal",
    tag: "ELEVAR / ORDENAR",
    text: "Pasó de gestionar múltiples negocios y prioridades a contar con mayor claridad, foco y visión estratégica sobre qué construir hacia adelante.",
  },
];

const includes = [
  "Diagnóstico",
  "Investigación y análisis",
  "Definición estratégica",
  "3 sesiones online de consultoría",
  "Documentación",
  "Construcción de la hoja de ruta",
  "Sistema de gobernanza",
  "Acompañamiento durante 60 días",
];

const before = [
  "Qué parte de su valor está siendo percibida",
  "Qué Capital de Marca está desaprovechando",
  "Qué percepción necesita construir",
  "Qué debería intervenir primero",
  "Dónde conviene invertir sus recursos",
];

const after = [
  "Qué Capital de Marca tiene",
  "Qué necesita construir, elevar o transferir",
  "Qué percepción necesita generar",
  "Qué activos debe activar y qué priorizar",
  "En qué orden intervenir y bajo qué criterios decidir",
];

const CapitalContent = () => {
  return (
    <div className="bg-background">
      {/* 01 · LO QUE JOTABE QUIERE */}
      <section id="persona" className="py-14 md:py-24 bg-muted/40">
        <div className="container px-4 max-w-4xl mx-auto">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }}>
            <Kicker>01 · El foco</Kicker>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-foreground leading-tight mb-5">
              Lograr que JotaBe sea elegida por algo más que producto, precio o plazo, poniendo en
              valor todo lo que construyeron durante casi 50 años.
            </h2>
            <p className="text-foreground/70 leading-relaxed mb-4">
              JotaBe ya es una empresa sólida: casi 50 años de trayectoria, presencia en seis provincias
              y el nodo de frío más grande del NEA. Pero ese valor real no se percibe del mismo modo
              en el mercado.
            </p>
            <p className="text-foreground/70 leading-relaxed mb-4">
              La comunicación todavía habla de producto, precio y logística. Y aunque esas variables
              siguen siendo importantes, no son —ni deben ser— el motivo por el que un cliente
              prefiere a JotaBe sobre cualquier otra opción.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              El objetivo es que JotaBe sea valorada por todo lo que representa y aporta al negocio
              de sus clientes: un aliado estratégico para su crecimiento, más allá del producto que
              compra.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-10 p-6 md:p-8 rounded-2xl border border-primary/30 bg-primary/[0.04]"
          >
            <p className="text-foreground/90 text-lg md:text-xl leading-relaxed font-medium">
              Fortalecer una relación de largo plazo en la que JotaBe sea percibida como un aliado
              estratégico para el crecimiento de sus clientes.
            </p>
            <p className="text-foreground/70 leading-relaxed mt-4">
              Haciendo que precio, plazo o producto sigan siendo relevantes, pero dejen de ser el
              primer criterio de elección.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 02 · PROMESA */}
      <section id="promesa" className="py-14 md:py-24 bg-primary/[0.05]">
        <div className="container px-4 max-w-3xl mx-auto">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }}>
            <Kicker>02 · ETAPAS</Kicker>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-foreground leading-tight mb-6">
              En 60 días, de decisiones fragmentadas a dirección estratégica.
            </h2>
            <p className="text-foreground/75 leading-relaxed mb-4">
              La empresa pasa a contar con una posición definida, una percepción objetivo y
              prioridades estratégicas claras para construir Capital de Marca con dirección y
              coherencia.
            </p>
            <p className="text-foreground/75 leading-relaxed">
              Como consecuencia, fortalece su capacidad de{" "}
              <span className="text-foreground font-medium">
                posicionarse, diferenciarse y ser elegida por el valor que realmente tiene.
              </span>
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-10 p-6 md:p-8 rounded-2xl border border-primary/30 bg-primary/[0.04]"
          >
            <p className="text-foreground/90 text-lg md:text-xl leading-relaxed font-medium">
              Construir una estrategia para que el cliente valore a JotaBe por todo lo que representa y aporta a su negocio, más allá del producto que compra.
            </p>
            <p className="text-foreground/70 leading-relaxed mt-4">
              Buscamos fortalecer una relación de largo plazo en la que JotaBe sea percibida como un aliado estratégico para el crecimiento de sus clientes, haciendo que variables como precio, plazo o producto sigan siendo relevantes, pero dejen de ser el primer criterio de elección.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 03 · MÉTODO */}
      <section id="metodo" className="py-14 md:py-24 bg-muted/40">
        <div className="container px-4 max-w-4xl mx-auto">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }}>
            <Kicker>03 · El método</Kicker>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-foreground leading-tight mb-5">
              La mayoría gestiona su marca empezando por las acciones.
            </h2>
            <p className="text-foreground/70 leading-relaxed mb-4">
              Necesitan vender más → hacen publicidad. Necesitan actualizarse → hacen un rebranding.
              Necesitan visibilidad → publican más. Necesitan posicionarse → contratan prensa. El
              founder necesita aparecer → construyen su marca personal.
            </p>
            <p className="text-foreground/70 leading-relaxed mb-10">
              El problema no es que esas acciones estén mal. Es decidir qué hacer antes de entender
              qué necesita construir la empresa. Por eso desarrollamos{" "}
              <span className="text-foreground font-semibold">CORESSEN CAPITAL®</span>: primero
              diagnosticamos qué movimiento necesita el Capital de Marca.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {movements.map((m, i) => (
              <motion.div
                key={m.name}
                {...fadeUp}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="p-6 rounded-2xl bg-card border border-border"
              >
                <p className="font-display text-xl font-bold text-primary tracking-wide mb-3">
                  {m.name}
                </p>
                <p className="text-sm text-foreground/70 leading-relaxed">{m.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5 }}
            className="mt-10 p-6 rounded-2xl border border-primary/30 bg-primary/[0.04]"
          >
            <p className="text-foreground/60 text-sm mb-2">No empezamos preguntando:</p>
            <p className="text-foreground/70 italic mb-4">“¿Qué deberíamos comunicar?”</p>
            <p className="text-foreground/60 text-sm mb-2">Empezamos preguntando:</p>
            <p className="text-foreground font-medium">
              “¿Qué Capital de Marca necesita construir esta empresa para la próxima etapa que quiere
              alcanzar?”
            </p>
          </motion.div>
        </div>
      </section>

      {/* 04 · PROCESO */}
      <section id="proceso" className="py-14 md:py-24 bg-[#0a1628]">
        <div className="container px-4 max-w-4xl mx-auto">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }} className="mb-10">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#60a5fa] font-semibold mb-4">
              04 · Proceso
            </p>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-white leading-tight">
              60 días, cinco pasos.
            </h2>
          </motion.div>

          <div className="space-y-4">
            {journey.map((s, i) => (
              <motion.div
                key={s.title}
                {...fadeUp}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                className="p-6 md:p-8 rounded-2xl border border-white/10 bg-white/[0.04]"
              >
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
                  <span className="text-[#60a5fa] text-xs tracking-[0.18em] font-semibold uppercase">
                    {s.step}
                  </span>
                  <span className="text-white/40 text-xs">{s.meta}</span>
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold text-white mb-3">
                  {s.title}
                </h3>
                <p className="text-white/65 text-sm md:text-base leading-relaxed mb-4">{s.body}</p>
                <p className="text-white/80 text-sm leading-relaxed border-l-2 border-[#60a5fa] pl-4">
                  <span className="text-[#60a5fa] font-semibold">Al cerrar: </span>
                  {s.close}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <GanttChart />

      {/* 05 · ENTREGABLES */}
      <section id="entregables" className="py-14 md:py-24 bg-muted/40">
        <div className="container px-4 max-w-5xl mx-auto">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }} className="mb-10">
            <Kicker>05 · Entregables</Kicker>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-foreground leading-tight">
              Qué recibe la empresa.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5">
            {deliverables.map((d, i) => (
              <motion.div
                key={d.title}
                {...fadeUp}
                transition={{ duration: 0.5, delay: (i % 2) * 0.05 }}
                className="p-6 rounded-2xl bg-card border border-border flex flex-col"
              >
                <p className="font-display text-2xl font-bold text-primary mb-3">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="text-foreground font-semibold text-lg mb-3">{d.title}</h3>
                <p className="text-foreground/70 text-sm leading-relaxed mb-4">{d.body}</p>
                <p className="text-foreground/60 text-sm leading-relaxed mt-auto pt-4 border-t border-border">
                  <span className="text-foreground font-medium">Qué resuelve. </span>
                  {d.solves}
                </p>
              </motion.div>
            ))}
          </div>

        </div>
      </section>


      {/* 06 · TRANSFORMACIÓN */}
      <section id="transformacion" className="py-14 md:py-24 bg-muted/40">
        <div className="container px-4 max-w-4xl mx-auto">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }} className="mb-10">
            <Kicker>07 · La transformación</Kicker>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-foreground leading-tight">
              De gestionar acciones a gobernar un activo.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5">
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.5 }}
              className="p-6 md:p-8 rounded-2xl bg-card border border-border"
            >
              <p className="text-[11px] tracking-[0.2em] font-semibold text-foreground/50 mb-4">
                ANTES
              </p>
              <p className="text-foreground/70 text-sm mb-4">La empresa tiene valor, pero no sabe:</p>
              <ul className="space-y-2 mb-5">
                {before.map((b) => (
                  <li key={b} className="text-foreground/70 text-sm leading-relaxed flex gap-2">
                    <span className="text-foreground/30 mt-0.5">—</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <p className="text-foreground/60 text-sm leading-relaxed">
                Por eso toma decisiones fragmentadas: branding por un lado, publicidad por otro,
                redes por otro, founder por otro, ventas por otro.
              </p>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ duration: 0.5, delay: 0.06 }}
              className="p-6 md:p-8 rounded-2xl border border-primary/35 bg-primary/[0.05]"
            >
              <p className="text-[11px] tracking-[0.2em] font-semibold text-primary mb-4">DESPUÉS</p>
              <p className="text-foreground/70 text-sm mb-4">La empresa sabe:</p>
              <ul className="space-y-2 mb-5">
                {after.map((a) => (
                  <li key={a} className="text-foreground/75 text-sm leading-relaxed flex gap-2">
                    <span className="text-primary mt-0.5">—</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
              <p className="text-foreground font-medium text-sm leading-relaxed">
                Deja de gestionar su marca como una suma de acciones y empieza a gobernarla como un
                activo estratégico de la empresa.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 08 · INVERSIÓN */}
      <section id="inversion" className="py-16 md:py-28 bg-[#0a1628]">
        <div className="container px-4 max-w-4xl mx-auto">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }}>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#60a5fa] font-semibold mb-4">
              08 · Inversión
            </p>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-white mb-4">
              Opciones de pago
            </h2>
            <p className="text-white/50 text-sm md:text-base mb-10 max-w-2xl">
              60 días de Dirección Estratégica de Capital de Marca: diagnóstico, definición
              estratégica, hoja de ruta y gobernanza.
            </p>

            <div className="grid md:grid-cols-2 gap-5 mb-8">
              {/* Opción 1 · Pago único */}
              <div className="relative group">
                <div
                  className="absolute -inset-0.5 rounded-2xl blur-sm opacity-40 transition-opacity duration-300 group-hover:opacity-60"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(96, 165, 250, 0.35) 0%, transparent 70%)",
                  }}
                />
                <div className="relative h-full rounded-2xl border border-[#60a5fa]/30 bg-white/[0.04] p-7 md:p-8 transition-colors duration-300">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#60a5fa] font-semibold mb-1">
                        Opción 01
                      </p>
                      <h3 className="font-display text-xl text-white font-semibold">
                        Pago único
                      </h3>
                    </div>
                    <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold text-[#60a5fa] px-2.5 py-1 rounded-full border border-[#60a5fa]/25 bg-[#60a5fa]/10">
                      Ahorro máximo
                    </span>
                  </div>

                  <div className="flex items-baseline gap-1.5 mb-2">
                    <span className="text-white/60 text-lg font-light">USD</span>
                    <span className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight">
                      8.000
                    </span>
                  </div>
                  <p className="text-white/45 text-sm mb-8">+ IVA · total&nbsp;</p>

                  <a
                    href="#proximo-paso"
                    className="block w-full text-center py-3.5 bg-[#60a5fa] hover:bg-[#3b82f6] text-[#0a1628] text-xs font-bold uppercase tracking-[0.12em] rounded-lg transition-colors duration-200"
                  >
                    Seleccionar plan
                  </a>
                </div>
              </div>

              {/* Opción 2 · Financiado */}
              <div className="relative h-full rounded-2xl border border-white/10 bg-white/[0.02] p-7 md:p-8 transition-colors duration-300 hover:border-white/20">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold mb-1">
                      Opción 02
                    </p>
                    <h3 className="font-display text-xl text-white/90 font-semibold">
                      Financiamiento
                    </h3>
                  </div>
                </div>

                <div className="flex flex-col mb-2">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-white/50 text-base font-light">2 x USD</span>
                    <span className="font-display text-3xl md:text-4xl font-semibold text-white/90 tracking-tight">
                      5.000
                    </span>
                  </div>
                </div>
                <p className="text-white/45 text-sm mb-2">+ IVA por cuota</p>
                <p className="text-white/35 text-xs mb-8">Total diferido: USD 10.000 + IVA</p>

                <a
                  href="#proximo-paso"
                  className="block w-full text-center py-3.5 border border-white/15 hover:border-[#60a5fa]/40 text-white/80 hover:text-white text-xs font-bold uppercase tracking-[0.12em] rounded-lg transition-colors duration-200"
                >
                  Elegir financiamiento
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 overflow-hidden mb-8">
              <div className="p-7 md:p-9">
                <p className="text-white/60 text-xs tracking-[0.2em] font-semibold mb-5">INCLUYE</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {includes.map((inc) => (
                    <div key={inc} className="flex gap-3 text-white/75 text-sm leading-relaxed">
                      <span className="text-[#60a5fa]">·</span>
                      <span>{inc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <p className="text-white/45 text-sm italic">
              Las implementaciones especializadas que surjan de la hoja de ruta y excedan el alcance
              estratégico de CORESSEN CAPITAL® se cotizan de manera independiente.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 09 · GARANTÍA */}
      <section id="garantia" className="py-14 md:py-24 bg-muted/40">
        <div className="container px-4 max-w-3xl mx-auto">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }}>
            <Kicker>09 · Garantía</Kicker>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-foreground leading-tight mb-6">
              Garantía de Dirección Estratégica
            </h2>
            <p className="text-foreground/70 leading-relaxed mb-4">
              CORESSEN se compromete a que al finalizar los 60 días la empresa cuente con un
              diagnóstico de su Capital de Marca, una Percepción Objetivo definida, una posición
              estratégica documentada y una hoja de ruta priorizada para construir, elevar o
              transferir su Capital de Marca.
            </p>
            <p className="text-foreground/70 leading-relaxed mb-4">
              Si habiendo participado de las instancias previstas y entregado la información
              necesaria alguno de estos componentes no estuviera resuelto al finalizar el período,
              extendemos el acompañamiento estratégico{" "}
              <span className="text-foreground font-medium">sin honorarios adicionales</span> hasta
              completarlo.
            </p>
            <p className="text-foreground/60 text-sm leading-relaxed">
              La garantía cubre aquello sobre lo que podemos asumir responsabilidad directa: la
              claridad, dirección y construcción estratégica. No garantiza resultados comerciales
              específicos cuya concreción dependa de implementación, mercado, equipo, inversión y
              otras variables externas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CIERRE */}
      <section id="proximo-paso" className="py-20 md:py-28 relative overflow-hidden bg-[#0a1628]">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 60% at 50% 100%, rgba(37, 99, 235, 0.25) 0%, transparent 70%)",
          }}
        />
        <div className="container px-4 max-w-3xl mx-auto relative z-10 text-center">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }}>
            <span className="text-[#60a5fa] text-xs tracking-[0.2em] font-semibold">
              EL PRÓXIMO PASO
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white mt-4 mb-8">
              Decidir primero. <span className="text-[#60a5fa]">Ejecutar después.</span>
            </h2>
            <p className="text-white/70 leading-relaxed mb-10">
              El primer paso concreto es una sesión de arranque para poner fecha al proceso.
              Quedamos a disposición para coordinarla.
            </p>

            <Button size="xl" asChild>
              <a href="#inversion">Coordinar sesión de arranque</a>
            </Button>

            <p className="mt-12 text-white/45 italic text-sm">
              Construimos marcas desde adentro hacia afuera. Primero el ser. Después la marca.
              Siempre en coherencia.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CapitalContent;
